import { useEffect, useLayoutEffect, useMemo, useState } from 'react';

import { FormType, GetInspectionFormsRequestDTO, GetInspectionFormsResponseDTO, MODULE, RecordState } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RowSelectionState } from '@tanstack/react-table';
import { useDebounce } from '@uidotdev/usehooks';
import { format } from 'date-fns';

import { ArchiveConfirmationDialog } from '@/components/common/archive-confirmation-dialog';
import { DeleteConfirmationDialog } from '@/components/common/delete-confirmation-dialog';
import { RestoreConfirmationDialog } from '@/components/common/restore-confirmation-dialog';
import { ShareDialog } from '@/components/common/share-dialog';
import ArchiveIcon from '@/components/icons/archive';
import CloneIcon from '@/components/icons/clone';
import DeleteIcon from '@/components/icons/delete';
import FolderOpenIcon from '@/components/icons/folder-open';
import FormTemplateIcon from '@/components/icons/form-template';
import GridIcon from '@/components/icons/grid';
import LinkViewIcon from '@/components/icons/link-view';
import PencilIcon from '@/components/icons/pencil';
import PlusIcon from '@/components/icons/plus';
import RegisterIcon from '@/components/icons/register';
import UndoIcon from '@/components/icons/undo';
import { TablePageGridView } from '@/components/table-page/table-page-grid-view';
import { TablePageHeader, TablePageHeaderActionButton } from '@/components/table-page/table-page-header';
import TablePageSearchBar from '@/components/table-page/table-page-search-bar';
import { TablePageSelectedCountIndicator } from '@/components/table-page/table-page-selected-count-indicator';
import { TablePageTitle } from '@/components/table-page/table-page-title';
import { useTopbarStore } from '@/components/topbar/topbar-store';
import { ButtonStateToggle } from '@/components/ui/button-state-toggle';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { useToast } from '@/components/ui/toaster';
import { env } from '@/env';
import { useInfiniteScrollTrigger } from '@/hooks/use-infinite-scroll-trigger';
import { usePageConfig } from '@/hooks/use-page-config';
import { usePermissions } from '@/hooks/use-permissions';
import { useResetRowSelectionOnChange } from '@/hooks/use-reset-row-selection-on-change';
import ShareIcon from '@/legacy/components/icons/share';
import { getInspectionFormsInfiniteQueryOptions, inspectionFormKeys } from '@/queries/forms';
import { ApiService } from '@/services/api-v2';
import { useModulesStore } from '@/stores/modules.store';
import { useUserStore } from '@/stores/user.store';
import { useEditFormNavigate, useWorkspaceNavigate } from '@/utils/routes';

import {
  InspectionFormsTable,
  NoInspectionFormsCreatedDisplay,
  NoInspectionFormsFoundFromFiltersDisplay,
  NoInspectionFormsFoundFromSearchDisplay,
} from './table';
import { createInspectionFormsTableColumns, FILTER_FIELD_MAP } from './table-meta';
import { TopbarActions } from './topbar-actions';

function InspectionFormsPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useWorkspaceNavigate();
  const handleEditInspectionForm = useEditFormNavigate();
  const { setModuleFilters, syncModuleColumns } = useModulesStore();
  const { canManageInspectionForms } = usePermissions();

  const { org } = useUserStore();

  const {
    config: { columnOrder, grouping, sorting, filters, view, columnVisibility },
    loading: isLoadingPageConfig,
    updatePageConfig,
  } = usePageConfig(MODULE.InspectionForms, {
    filterFieldMap: FILTER_FIELD_MAP,
    columns: createInspectionFormsTableColumns({ canManageInspectionForms }),
  });

  const [openArchiveConfirmation, setOpenArchiveConfirmation] = useState(false);
  const [openRestoreConfirmation, setOpenRestoreConfirmation] = useState(false);
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);
  const [inspectionFormState, setInspectionFormState] = useState<RecordState>(RecordState.Active);
  const [shareUrl, setShareUrl] = useState<string | null>(null);

  useLayoutEffect(() => {
    useTopbarStore.getState().setActions(<TopbarActions />);

    return () => {
      useTopbarStore.getState().setActions(<></>);
    };
  }, []);

  const {
    data: inspectionFormsData,
    fetchNextPage: fetchNextInspectionFormsPage,
    hasNextPage: hasNextInspectionFormsPage,
    isFetching: isFetchingInspectionForms,
    isFetchingNextPage: isFetchingNextInspectionFormsPage,
    error: inspectionFormsError,
  } = useInfiniteQuery(
    getInspectionFormsInfiniteQueryOptions({
      filters: {
        state: inspectionFormState,
        search: debouncedSearch,
        fields: Object.fromEntries(
          Object.entries(FILTER_FIELD_MAP).map(([id, key]) => [
            key,
            id === 'number'
              ? ((filters || []).find((filter) => filter.id === id)?.value as string)?.replace(/[^0-9]/g, '')
              : (filters || []).find((filter) => filter.id === id)?.value,
          ]),
        ),
      },
      orderBy: sorting.key
        ? { field: sorting.key as NonNullable<GetInspectionFormsRequestDTO['orderBy']>['field'], direction: sorting.direction as 'asc' | 'desc' }
        : undefined,
      enabled: !isLoadingPageConfig,
    }),
  );

  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);
  const { sentinelRef, canLoadMore, loadMore } = useInfiniteScrollTrigger({
    fetchNextPage: fetchNextInspectionFormsPage,
    hasNextPage: hasNextInspectionFormsPage,
    isFetching: isFetchingInspectionForms,
    isError: !!inspectionFormsError,
    root: scrollRoot,
  });

  const getSelectedInspectionForms = useMemo(() => {
    return (incomingData: typeof inspectionFormsData) => {
      if (!incomingData?.pages) return [];
      const ids = Object.keys(selectedRows);
      const inspectionForms = incomingData.pages.flatMap((page) => page.data);
      return ids.filter((id) => !isNaN(parseInt(id))).map((id) => inspectionForms[parseInt(id)]);
    };
  }, [selectedRows]);

  const { mutateAsync: bulkUpdateFormState, isPending: isUpdatingFormState } = useMutation({
    mutationFn: async ({ ids, state }: { ids: string[]; state: RecordState }) => await ApiService.BulkUpdateFormState({ ids, state }),
    onSuccess: (_, { state }) => {
      const messages = {
        [RecordState.Active]: 'Inspection forms restored successfully',
        [RecordState.Archived]: 'Inspection forms archived successfully',
        [RecordState.Deleted]: 'Inspection forms deleted successfully',
      };
      toast.success(messages[state]);
      setOpenArchiveConfirmation(false);
      setOpenRestoreConfirmation(false);
      setOpenDeleteConfirmation(false);
      setSelectedRows({});

      queryClient.invalidateQueries({ queryKey: inspectionFormKeys.all });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  function handleArchiveInspectionForms() {
    if (inspectionFormsData?.pages) {
      const toArchive = getSelectedInspectionForms(inspectionFormsData);
      bulkUpdateFormState({ ids: toArchive.map((inspectionForm) => inspectionForm.id), state: RecordState.Archived });
    }
  }

  function handleRestoreInspectionForms() {
    if (inspectionFormsData?.pages) {
      const toRestore = getSelectedInspectionForms(inspectionFormsData);
      bulkUpdateFormState({ ids: toRestore.map((inspectionForm) => inspectionForm.id), state: RecordState.Active });
    }
  }

  function handleDeleteInspectionForms() {
    if (inspectionFormsData?.pages) {
      const toDelete = getSelectedInspectionForms(inspectionFormsData);
      bulkUpdateFormState({ ids: toDelete.map((inspectionForm) => inspectionForm.id), state: RecordState.Deleted });
    }
  }

  const { mutate: cloneInspectionForms, isPending: isCloningInspectionForms } = useMutation({
    mutationFn: async (formIds: string[]) => await ApiService.CloneForms({ formIds }),
    onSuccess: () => {
      toast.success('Inspection forms cloned successfully');
      setSelectedRows({});

      queryClient.invalidateQueries({ queryKey: inspectionFormKeys.all });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  function handleCloneInspectionForms() {
    cloneInspectionForms(getSelectedInspectionForms(inspectionFormsData).map((inspectionForm) => inspectionForm.id));
  }

  function handleAddInspectionForm() {
    navigate(`/forms/create?formType=${FormType.InspectionForm}`);
  }

  function handleGridItemClick(item: GetInspectionFormsResponseDTO['data'][number]) {
    navigate(`/inspections/forms/${item.id}/submissions`);
  }

  const selectedInspectionForms = useMemo(() => getSelectedInspectionForms(inspectionFormsData), [inspectionFormsData, getSelectedInspectionForms]);

  useEffect(() => {
    if (inspectionFormsError) {
      toast.error(inspectionFormsError.message);
    }
  }, [toast, inspectionFormsError]);

  useResetRowSelectionOnChange(setSelectedRows, [inspectionFormState, debouncedSearch, filters, sorting.key, sorting.direction, grouping]);

  const flattenedData = useMemo(() => inspectionFormsData?.pages.flatMap((page) => page.data) ?? [], [inspectionFormsData]);

  useEffect(() => {
    if (!view) {
      updatePageConfig({
        view: 'grid',
      });
    }
  }, [view, updatePageConfig]);

  useEffect(() => {
    syncModuleColumns(MODULE.InspectionForms, createInspectionFormsTableColumns());
  }, [syncModuleColumns]);

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <TablePageHeader>
        <div className="no-scrollbar h-full w-full overflow-x-auto py-0.5">
          <div className="flex h-full min-w-[580px] items-center justify-between">
            {!selectedInspectionForms.length && (
              <>
                <div className="flex h-full items-center gap-x-2 px-2">
                  <TablePageTitle>Forms</TablePageTitle>
                  <ButtonStateToggle
                    options={[
                      { icon: <FolderOpenIcon />, label: 'Active', value: RecordState.Active },
                      { icon: <ArchiveIcon />, label: 'Archived', value: RecordState.Archived },
                    ]}
                    value={inspectionFormState}
                    onChange={setInspectionFormState}
                    className="font-medium"
                    responsive
                  />
                  <ButtonStateToggle
                    options={[
                      { icon: <GridIcon />, label: 'Grid', value: 'grid' },
                      { icon: <RegisterIcon />, label: 'Register', value: 'register' },
                    ]}
                    value={view}
                    onChange={(view) =>
                      updatePageConfig({
                        view: view as 'grid' | 'register',
                      })
                    }
                    className="font-medium"
                    responsive
                  />
                  <TablePageSearchBar search={search} setSearch={setSearch} />
                  {filters && filters.length > 0 && (
                    <TablePageHeaderActionButton
                      icon={<UndoIcon />}
                      label="Remove all Filters"
                      kind="secondary"
                      onClick={() => setModuleFilters(MODULE.InspectionForms, [])}
                    />
                  )}
                </div>

                <div className="mr-1 flex h-full items-center">
                  <TablePageHeaderActionButton
                    onClick={() => setShareUrl(`${env.VITE_FORMS_URL}/${org?.id}/inspection`)}
                    icon={<ShareIcon />}
                    label="Share Link"
                  />
                  <TablePageHeaderActionButton
                    onClick={() => window.open(`${env.VITE_FORMS_URL}/${org?.id}/inspection`, '_blank')}
                    icon={<LinkViewIcon />}
                    label="Open Operator Web App"
                  />
                  {canManageInspectionForms && (
                    <TablePageHeaderActionButton
                      variant="success"
                      icon={<PlusIcon />}
                      kind="primary"
                      onClick={handleAddInspectionForm}
                      label="Create Inspection Form"
                    />
                  )}
                </div>
              </>
            )}

            {selectedInspectionForms.length > 0 && canManageInspectionForms && (
              <div className="flex items-center gap-1 px-2">
                <TablePageSelectedCountIndicator selectedCount={selectedInspectionForms.length} totalCount={inspectionFormsData?.pages[0]?.totalCount ?? 0} />
                {selectedInspectionForms.length === 1 && (
                  <TablePageHeaderActionButton
                    icon={<PencilIcon />}
                    label="Edit Form"
                    onClick={() => handleEditInspectionForm(selectedInspectionForms[0].id)}
                  />
                )}

                {selectedInspectionForms.length > 0 && (
                  <TablePageHeaderActionButton
                    icon={<CloneIcon />}
                    label={selectedInspectionForms.length > 1 ? 'Clone Forms' : 'Clone Form'}
                    disabled={isCloningInspectionForms}
                    onClick={handleCloneInspectionForms}
                  />
                )}

                {inspectionFormState === RecordState.Active && (
                  <TablePageHeaderActionButton icon={<ArchiveIcon />} label="Archive Selected" onClick={() => setOpenArchiveConfirmation(true)} />
                )}
                {inspectionFormState === RecordState.Archived && (
                  <>
                    <TablePageHeaderActionButton icon={<UndoIcon />} label="Restore Selected" onClick={() => setOpenRestoreConfirmation(true)} />
                    <TablePageHeaderActionButton
                      icon={<DeleteIcon className="text-default-red" />}
                      label="Delete Selected"
                      onClick={() => setOpenDeleteConfirmation(true)}
                    />
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </TablePageHeader>

      {view === 'register' && (
        <ScrollArea type="always" viewportRef={setScrollRoot} className="relative flex flex-1 flex-col">
          <ScrollBar orientation="horizontal" />
          <InspectionFormsTable
            data={flattenedData}
            loading={(isFetchingInspectionForms && !isFetchingNextInspectionFormsPage) || isLoadingPageConfig}
            notFoundDisplay={
              debouncedSearch.length > 0 ? (
                <NoInspectionFormsFoundFromSearchDisplay />
              ) : filters && filters.length > 0 ? (
                <NoInspectionFormsFoundFromFiltersDisplay
                  addForm={handleAddInspectionForm}
                  removeColumnFilters={() => setModuleFilters(MODULE.InspectionForms, [])}
                />
              ) : (
                <NoInspectionFormsCreatedDisplay addForm={handleAddInspectionForm} />
              )
            }
            rowSelection={selectedRows}
            onRowSelectionChange={setSelectedRows}
            columnFilters={filters || []}
            onColumnFiltersChange={(filters) => setModuleFilters(MODULE.InspectionForms, filters)}
            sorting={sorting}
            grouping={grouping}
            columnOrder={columnOrder}
            columnVisibility={columnVisibility}
            onSortingChange={(newSorting) =>
              updatePageConfig({
                table: { sorting: newSorting },
              })
            }
          />
          {hasNextInspectionFormsPage && (
            <Button ref={sentinelRef} onClick={loadMore} disabled={!canLoadMore} className="mx-auto w-full text-center" variant="ghost">
              {isFetchingNextInspectionFormsPage ? 'Loading more...' : 'Load Newer'}
            </Button>
          )}
        </ScrollArea>
      )}

      {view === 'grid' && (
        <div ref={setScrollRoot} className="scrollbar scrollbar-track-transparent scrollbar-thumb-grey-5 overflow-y-auto">
          <TablePageGridView<GetInspectionFormsResponseDTO['data'][number]>
            data={flattenedData}
            notFoundDisplay={<NoInspectionFormsCreatedDisplay addForm={handleAddInspectionForm} />}
            loading={(isFetchingInspectionForms && !isFetchingNextInspectionFormsPage) || isLoadingPageConfig}
            ItemComponent={({ item, handleSelectedItemsChange, index }) => (
              <div className="flex flex-col gap-2 p-2">
                <div className="flex w-full justify-between">
                  <div className="flex items-center gap-[2px]">
                    <FormTemplateIcon />
                  </div>
                  <label className="-m-2 cursor-pointer p-2" onClick={(e) => e.stopPropagation()}>
                    <Checkbox checked={selectedRows[index]} onCheckedChange={() => handleSelectedItemsChange(index)} />
                  </label>
                </div>

                <div className="flex flex-col gap-2">
                  <div>
                    <div className="line-clamp-2 min-h-[2lh] text-sm font-[700] break-words">{item.name}</div>
                    <div className="text-secondary-text text-xs">FR{item.number}</div>
                  </div>

                  <div className="text-secondary-text text-xs">
                    <div>Forms: {item.submissionCount}</div>
                    <div>Version: {item.revision}</div>
                    <div>Updated: {format(new Date(item.updatedAt), 'MMM dd, yyyy')}</div>
                  </div>
                </div>
              </div>
            )}
            selectedItems={selectedRows}
            onSelectedItemsChange={setSelectedRows}
            onItemClick={handleGridItemClick}
          />
          {hasNextInspectionFormsPage && (
            <Button ref={sentinelRef} onClick={loadMore} disabled={!canLoadMore} className="mx-auto w-full text-center" variant="ghost">
              {isFetchingNextInspectionFormsPage ? 'Loading more...' : 'Load Newer'}
            </Button>
          )}
        </div>
      )}

      <ArchiveConfirmationDialog
        open={openArchiveConfirmation}
        onOpenChange={setOpenArchiveConfirmation}
        callback={handleArchiveInspectionForms}
        loading={isUpdatingFormState}
        title="Archive inspection forms?"
        description={
          <>
            Are you sure you want to archive these inspection forms? You can restore them later from the archived tab.
            {selectedInspectionForms.some((form) => form.deploymentRuleCount > 0) && (
              <>
                <br />
                <br />
                <b>Note:</b> Archiving forms that are in deployment rules will remove them from those rules. You can restore forms later from the Archived tab.
              </>
            )}
          </>
        }
      />
      <RestoreConfirmationDialog
        open={openRestoreConfirmation}
        onOpenChange={setOpenRestoreConfirmation}
        callback={handleRestoreInspectionForms}
        loading={isUpdatingFormState}
        title="Restore inspection forms?"
        description="Are you sure you want to restore these inspection forms? They will be moved back to your active inspection forms."
      />
      <DeleteConfirmationDialog
        open={openDeleteConfirmation}
        onOpenChange={setOpenDeleteConfirmation}
        callback={handleDeleteInspectionForms}
        loading={isUpdatingFormState}
        title="Delete inspection forms?"
        description="This action is permanent and cannot be undone. Are you sure you want to delete these inspection forms?"
      />

      {shareUrl && <ShareDialog url={shareUrl} onOpenChange={() => setShareUrl(null)} open={!!shareUrl} />}
    </div>
  );
}

export default InspectionFormsPage;
